import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import JudgeCard from "@/components/JudgeCard";
import { Search, Trophy, Users, Star, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [featuredJudges, setFeaturedJudges] = useState([]);
  const [recentlyReviewedJudges, setRecentlyReviewedJudges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJudges();
  }, []);

  const fetchJudges = async () => {
    try {
      const [featuredRes, recentlyReviewedRes] = await Promise.all([
        axios.get(`${API}/judges/featured`),
        axios.get(`${API}/judges/recently-reviewed`),
      ]);
      setFeaturedJudges(featuredRes.data);
      setRecentlyReviewedJudges(recentlyReviewedRes.data);
    } catch (error) {
      console.error("Failed to fetch judges:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/judges?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-navy overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/1464856/pexels-photo-1464856.jpeg?auto=compress&cs=tinysrgb&w=1920')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Find & Rate{" "}
              <span className="text-gold">AKC Hunt Test</span> &{" "}
              <span className="text-gold">Field Trial</span> Judges
            </h1>
            <p className="text-lg text-white/80 mb-8 leading-relaxed">
              The premier platform for the hunting dog community to share experiences 
              and help others make informed decisions about judges.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search judges by name or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-14 pl-12 pr-4 text-lg border-2 border-transparent focus:border-gold bg-white"
                  data-testid="hero-search-input"
                />
              </div>
              <Button
                type="submit"
                className="h-14 px-8 bg-gold text-navy font-semibold hover:bg-gold/90"
                data-testid="hero-search-btn"
              >
                Search
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-12 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-8 text-center">
            <div>
              <div className="flex justify-center mb-2">
                <Users className="w-8 h-8 text-gold" />
              </div>
              <p className="font-heading text-3xl font-bold text-navy">
                {recentlyReviewedJudges.length}+
              </p>
              <p className="text-slate-600">Judges Listed</p>
            </div>
            <div>
              <div className="flex justify-center mb-2">
                <Star className="w-8 h-8 text-gold" />
              </div>
              <p className="font-heading text-3xl font-bold text-navy">
                {featuredJudges.reduce((acc, j) => acc + (j.review_count || 0), 0)}+
              </p>
              <p className="text-slate-600">Reviews</p>
            </div>
            <div>
              <div className="flex justify-center mb-2">
                <Trophy className="w-8 h-8 text-gold" />
              </div>
              <p className="font-heading text-3xl font-bold text-navy">AKC</p>
              <p className="text-slate-600">Certified Events</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Judges */}
      {featuredJudges.length > 0 && (
        <section className="py-16 bg-off-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-3xl font-semibold text-navy">
                Top Rated Judges
              </h2>
              <Button
                variant="outline"
                className="border-navy text-navy hover:bg-navy hover:text-white"
                onClick={() => navigate("/judges")}
                data-testid="view-all-judges-btn"
              >
                View All
              </Button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredJudges.map((judge) => (
                <JudgeCard key={judge.id} judge={judge} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Recently Reviewed Judges */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading text-3xl font-semibold text-navy flex items-center gap-3">
              <MessageSquare className="w-8 h-8 text-gold" />
              Recently Reviewed
            </h2>
            <Button
              variant="outline"
              className="border-navy text-navy hover:bg-navy hover:text-white"
              onClick={() => navigate("/judges")}
              data-testid="view-all-judges-btn-2"
            >
              View All Judges
            </Button>
          </div>
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="bg-slate-100 animate-pulse h-48 rounded-sm"
                />
              ))}
            </div>
          ) : recentlyReviewedJudges.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {recentlyReviewedJudges.map((judge) => (
                <JudgeCard key={judge.id} judge={judge} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-slate-50 rounded-sm">
              <MessageSquare className="w-12 h-12 text-gold mx-auto mb-4" />
              <h3 className="font-heading text-xl text-navy mb-2">
                No reviews yet
              </h3>
              <p className="text-slate-600">
                Be the first to review a judge!
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-navy py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Share Your Experience
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Help the hunting dog community by sharing your experiences with judges.
            Your anonymous reviews help others make better decisions.
          </p>
          <Button
            onClick={() => navigate("/judges")}
            className="bg-gold text-navy hover:bg-gold/90 font-semibold px-8 py-3 text-lg"
            data-testid="cta-browse-btn"
          >
            Browse Judges & Leave a Review
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-heading text-xl text-gold mb-2">RateMyJudges.org</p>
          <p className="text-slate-400 text-sm mb-4">
            The community-driven platform for AKC Hunt Test & Field Trial judge reviews.
          </p>
          <div className="border-t border-slate-700 pt-4 mt-4">
            <p className="text-slate-400 text-sm">
              <span className="text-gold font-medium">Contact Us:</span>{" "}
              <a 
                href="mailto:ratemyjudges@gmail.com" 
                className="text-white hover:text-gold transition-colors"
                data-testid="contact-email"
              >
                ratemyjudges@gmail.com
              </a>
            </p>
          </div>
        </div>
      </footer>

          </div>
  );
}
